import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import './CompletarDatos.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PREGUNTAS_COGNITIVAS = [
  {
    id: 'pregunta1',
    texto: '¿Usa teléfono móvil o tablet?',
    opciones: [
      { emoji: '📱', texto: 'Sí, usa WhatsApp y apps', valor: 3 },
      { emoji: '📞', texto: 'Solo para llamar', valor: 2 },
      { emoji: '❌', texto: 'No usa o casi nunca', valor: 1 },
    ]
  },
  {
    id: 'pregunta2',
    texto: '¿Lee mensajes en pantalla sin ayuda?',
    opciones: [
      { emoji: '👓', texto: 'Sí, sin problema', valor: 3 },
      { emoji: '🤏', texto: 'Con letra grande sí', valor: 2 },
      { emoji: '🙋', texto: 'Necesita que se lo lean', valor: 1 },
    ]
  },
  {
    id: 'pregunta3',
    texto: '¿Puede seguir pasos sencillos en pantalla?',
    subtexto: 'ej: "toca aquí"',
    opciones: [
      { emoji: '✅', texto: 'Sí, sin problema', valor: 3 },
      { emoji: '🔄', texto: 'Si se lo explican antes', valor: 2 },
      { emoji: '❌', texto: 'Le cuesta mucho', valor: 1 },
    ]
  },
  {
    id: 'pregunta4',
    texto: '¿Cómo se comunica mejor?',
    opciones: [
      { emoji: '✍️', texto: 'Escribiendo', valor: 3 },
      { emoji: '🗣️', texto: 'Hablando', valor: 2 },
      { emoji: '👂', texto: 'Solo escuchando', valor: 1 },
    ]
  },
];

const ACTIVIDADES = [
  { id: 'ejercicio', emoji: '💪', texto: 'Ejercicio' },
  { id: 'tv', emoji: '📺', texto: 'Ver la tele' },
  { id: 'compra', emoji: '🛒', texto: 'Hacer la compra' },
  { id: 'audiolibros', emoji: '📖', texto: 'Audiolibros' },
  { id: 'musica', emoji: '🎵', texto: 'Música' },
  { id: 'taxi', emoji: '🚕', texto: 'Taxi' },
];

const CompletarDatos = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: abueloIdParam } = useParams();
  const { getAccessTokenSilently } = useAuth0();

  const sessionId = searchParams.get('session_id');
  const isEditMode = !!abueloIdParam;

  const [formData, setFormData] = useState({
    nombre: '',
    apellidos: '',
    telefono: '',
    direccion: '',
  });

  const [nacDia, setNacDia] = useState('');
  const [nacMes, setNacMes] = useState('');
  const [nacAnio, setNacAnio] = useState('');

  const [testCognitivo, setTestCognitivo] = useState({});
  const [tareasActivas, setTareasActivas] = useState([]);
  const [monederoActivo, setMonederoActivo] = useState(false);
  const [temasInteres, setTemasInteres] = useState('');
  const [notasExtra, setNotasExtra] = useState('');

  const [aceptaTerminos, setAceptaTerminos] = useState(isEditMode);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(!isEditMode);
  const [loadingEdit, setLoadingEdit] = useState(isEditMode);

  // Puntuación cognitiva
  const puntuacionTotal = Object.values(testCognitivo).reduce((sum, val) => sum + val, 0);
  const todasRespondidas = Object.keys(testCognitivo).length === 4;

  const getNivelCognitivo = () => {
    if (!todasRespondidas) return null;
    if (puntuacionTotal >= 10) return 'autonomo';
    if (puntuacionTotal >= 7) return 'semi_autonomo';
    return 'necesita_acompanamiento';
  };

  const getResultadoTexto = () => {
    const nivel = getNivelCognitivo();
    if (!nivel) return null;
    if (nivel === 'autonomo') return { texto: '🟢 Puede usar la app con autonomía', clase: 'resultado-verde' };
    if (nivel === 'semi_autonomo') return { texto: '🟡 Puede necesitar ayuda inicial', clase: 'resultado-amarillo' };
    return { texto: '🟠 Se recomienda modo simplificado', clase: 'resultado-naranja' };
  };

  // Modo registro: verificar pago
  useEffect(() => {
    if (isEditMode) return;
    if (!sessionId) {
      setError('No se encontró sesión de pago');
      setVerifying(false);
      return;
    }
    verificarPago();
  }, [sessionId, isEditMode]);

  // Modo editar: cargar datos
  useEffect(() => {
    if (!isEditMode) return;
    cargarDatosAbuelo();
  }, [isEditMode, abueloIdParam]);

  const verificarPago = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get(
        `${API_URL}/api/stripe/verify-session/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.data.success) {
        setError('El pago no se completó correctamente');
      }
    } catch (err) {
      console.error('Error verificando pago:', err);
      setError('Error al verificar el pago');
    } finally {
      setVerifying(false);
    }
  };

  const cargarDatosAbuelo = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get(
        `${API_URL}/api/abuelos/${abueloIdParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const abuelo = response.data;

      setFormData({
        nombre: abuelo.nombre || '',
        apellidos: abuelo.apellidos || '',
        telefono: abuelo.telefono || '',
        direccion: abuelo.direccion || '',
      });

      if (abuelo.fecha_nacimiento) {
        const d = new Date(abuelo.fecha_nacimiento);
        setNacDia(String(d.getDate()));
        setNacMes(String(d.getMonth() + 1));
        setNacAnio(String(d.getFullYear()));
      }

      const prefs = abuelo.preferencias || {};
      if (prefs.test_cognitivo) setTestCognitivo(prefs.test_cognitivo);
      if (prefs.tareas_activas) setTareasActivas(prefs.tareas_activas);
      if (prefs.monedero_activo) setMonederoActivo(prefs.monedero_activo);
      if (prefs.temas_interes) setTemasInteres(prefs.temas_interes);

      setNotasExtra(abuelo.notas_medicas || '');
      setAceptaTerminos(true);
    } catch (err) {
      console.error('Error cargando datos:', err);
      setError('Error al cargar los datos del familiar');
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const toggleActividad = (id) => {
    setTareasActivas(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isEditMode && !aceptaTerminos) {
      setError('Debes aceptar los Términos y Condiciones para continuar');
      return;
    }

    setLoading(true);

    try {
      const token = await getAccessTokenSilently();

      const preferencias = {
        nivel_cognitivo: getNivelCognitivo() || undefined,
        test_cognitivo: Object.keys(testCognitivo).length > 0 ? testCognitivo : undefined,
        tareas_activas: tareasActivas.length > 0 ? tareasActivas : undefined,
        monedero_activo: monederoActivo,
        temas_interes: temasInteres || undefined,
      };

      if (isEditMode) {
        const updateData = {
          nombre: formData.nombre,
          apellidos: formData.apellidos,
          telefono: formData.telefono,
          preferencias,
          notas_medicas: notasExtra || null,
          medicamentos: null,
          contacto_emergencia: null,
          telefono_emergencia: null,
        };
        if (nacAnio && nacMes && nacDia) {
          updateData.fecha_nacimiento = `${nacAnio}-${nacMes.padStart(2, '0')}-${nacDia.padStart(2, '0')}`;
        }
        if (formData.direccion) updateData.direccion = formData.direccion;

        await axios.put(
          `${API_URL}/api/abuelos/${abueloIdParam}`,
          updateData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        const abueloData = {
          sessionId,
          nombre: formData.nombre,
          apellidos: formData.apellidos,
          telefono: formData.telefono,
          preferencias,
          acepta_terminos: true,
          fecha_aceptacion_terminos: new Date().toISOString(),
          notas_medicas: notasExtra || null,
          medicamentos: null,
          contacto_emergencia: null,
          telefono_emergencia: null,
        };
        if (nacAnio && nacMes && nacDia) {
          abueloData.fecha_nacimiento = `${nacAnio}-${nacMes.padStart(2, '0')}-${nacDia.padStart(2, '0')}`;
        }
        if (formData.direccion) abueloData.direccion = formData.direccion;

        await axios.post(
          `${API_URL}/api/abuelos/post-pago`,
          abueloData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      navigate('/dashboard');
    } catch (err) {
      console.error('Error al guardar:', err);
      setError(err.response?.data?.error || 'Error al guardar los datos');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="registrar-abuelo-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Verificando tu pago...</p>
        </div>
      </div>
    );
  }

  if (loadingEdit) {
    return (
      <div className="registrar-abuelo-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando datos...</p>
        </div>
      </div>
    );
  }

  if (error && !sessionId && !isEditMode) {
    return (
      <div className="registrar-abuelo-container">
        <div className="error-state">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/dashboard')} className="btn-primary-large">
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  const resultado = getResultadoTexto();

  return (
    <div className="registrar-abuelo-container">
      <div className="registrar-header">
        {isEditMode && (
          <button className="btn-back" onClick={() => navigate('/dashboard')}>
            ← Volver al Dashboard
          </button>
        )}
        {!isEditMode && <div className="success-badge">Pago completado</div>}
        <h1>{isEditMode ? `Editar datos de ${formData.nombre || 'tu familiar'}` : 'Completa los datos de tu familiar'}</h1>
        <p>{isEditMode
          ? 'Actualiza la información de tu familiar'
          : 'Ya casi está todo listo. Solo falta que completes la información de tu familiar para que Paquita pueda comenzar a llamarle'
        }</p>
      </div>

      <form onSubmit={handleSubmit} className="registrar-form">
        {error && <div className="error-message">{error}</div>}

        {/* A) Información Personal */}
        <div className="form-section">
          <h2>Información Personal</h2>

          <div className="form-row">
            <div className="form-group">
              <label>Nombre *</label>
              <input type="text" name="nombre" value={formData.nombre} onChange={handleChange} required placeholder="Nombre del familiar" />
            </div>
            <div className="form-group">
              <label>Apellidos *</label>
              <input type="text" name="apellidos" value={formData.apellidos} onChange={handleChange} required placeholder="Apellidos" />
            </div>
          </div>

          <div className="form-group">
            <label>Fecha de nacimiento</label>
            <div className="fecha-selects">
              <select value={nacDia} onChange={(e) => setNacDia(e.target.value)}>
                <option value="">Día</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={String(d)}>{d}</option>
                ))}
              </select>
              <select value={nacMes} onChange={(e) => setNacMes(e.target.value)}>
                <option value="">Mes</option>
                {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'].map((m, i) => (
                  <option key={i} value={String(i + 1)}>{m}</option>
                ))}
              </select>
              <select value={nacAnio} onChange={(e) => setNacAnio(e.target.value)}>
                <option value="">Año</option>
                {Array.from({ length: 127 }, (_, i) => 2026 - i).map(y => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Teléfono *</label>
            <input type="tel" name="telefono" value={formData.telefono} onChange={handleChange} required placeholder="+34 XXX XXX XXX" />
          </div>

          <div className="form-group">
            <label>Dirección</label>
            <input type="text" name="direccion" value={formData.direccion} onChange={handleChange} placeholder="Dirección completa" />
          </div>
        </div>

        {/* C) Test Cognitivo */}
        <div className="form-section">
          <h2>🧠 Ayúdanos a conocer a tu ser querido</h2>
          <p className="section-subtitle">Estas preguntas nos ayudan a adaptar Paquita a sus necesidades. Contesta pensando en cómo se desenvuelve actualmente.</p>

          {PREGUNTAS_COGNITIVAS.map((pregunta) => (
            <div key={pregunta.id} className="test-pregunta">
              <label>
                {pregunta.texto}
                {pregunta.subtexto && <span className="pregunta-subtexto"> ({pregunta.subtexto})</span>}
              </label>
              <div className="test-opciones">
                {pregunta.opciones.map((opcion) => (
                  <button
                    key={opcion.valor}
                    type="button"
                    className={`test-opcion ${testCognitivo[pregunta.id] === opcion.valor ? 'test-opcion-selected' : ''}`}
                    onClick={() => setTestCognitivo({ ...testCognitivo, [pregunta.id]: opcion.valor })}
                  >
                    <span className="opcion-emoji">{opcion.emoji}</span>
                    <span className="opcion-texto">{opcion.texto}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}

          {resultado && (
            <div className={`test-resultado ${resultado.clase}`}>
              {resultado.texto}
            </div>
          )}

          <div className="form-group" style={{ marginTop: '1.5rem' }}>
            <label>¿Algo más que debamos saber?</label>
            <textarea
              value={notasExtra}
              onChange={(e) => setNotasExtra(e.target.value)}
              rows="3"
              placeholder="Cualquier detalle que nos ayude a cuidarle mejor..."
            />
          </div>
        </div>

        {/* D) Actividades preferidas */}
        <div className="form-section">
          <h2>🎯 Actividades preferidas</h2>
          <p className="section-subtitle">Selecciona las actividades que le gustan. Paquita se las ofrecerá durante sus conversaciones.</p>

          <div className="actividades-grid">
            {ACTIVIDADES.map((act) => (
              <button
                key={act.id}
                type="button"
                className={`actividad-chip ${tareasActivas.includes(act.id) ? 'actividad-chip-selected' : ''}`}
                onClick={() => toggleActividad(act.id)}
              >
                {act.emoji} {act.texto}
              </button>
            ))}
          </div>

          <div className="monedero-toggle">
            <div className="monedero-info">
              <span className="monedero-label">💰 Activar monedero</span>
              <span className="monedero-desc">Permite actividades que pueden tener coste (taxi, compras...)</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={monederoActivo}
                onChange={(e) => setMonederoActivo(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="form-group" style={{ marginTop: '1.5rem' }}>
            <label>Temas de conversación favoritos</label>
            <input
              type="text"
              value={temasInteres}
              onChange={(e) => setTemasInteres(e.target.value)}
              placeholder="Ej: familia, jardinería, cocina, historia..."
            />
          </div>
        </div>

        {/* E) Términos y Condiciones - solo en registro */}
        {!isEditMode && (
          <div className="form-section" style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '12px', border: '1px solid #e9ecef' }}>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={aceptaTerminos}
                  onChange={(e) => setAceptaTerminos(e.target.checked)}
                  style={{ marginTop: '4px', width: '20px', height: '20px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px', lineHeight: '1.5', color: '#333' }}>
                  He leído y acepto los{' '}
                  <a href="/legal/terminos-condiciones.pdf" target="_blank" rel="noopener noreferrer" style={{ color: '#8b5cf6', textDecoration: 'underline' }}>
                    Términos y Condiciones
                  </a>{' '}
                  y la{' '}
                  <a href="/legal/politica-privacidad.pdf" target="_blank" rel="noopener noreferrer" style={{ color: '#8b5cf6', textDecoration: 'underline' }}>
                    Política de Privacidad
                  </a>.
                  <br />
                  <strong>Declaro que tengo el consentimiento de mi familiar para facilitar sus datos personales y que le he informado sobre el tratamiento de los mismos.</strong>
                </span>
              </label>
            </div>
          </div>
        )}

        <div className="form-actions">
          {isEditMode && (
            <button type="button" className="btn-cancel" onClick={() => navigate('/dashboard')}>
              Cancelar
            </button>
          )}
          <button
            type="submit"
            className="btn-submit btn-primary-large"
            disabled={loading || (!isEditMode && !aceptaTerminos)}
            style={{ opacity: ((!isEditMode && !aceptaTerminos) || loading) ? 0.5 : 1 }}
          >
            {loading
              ? (isEditMode ? 'Guardando...' : 'Registrando...')
              : (isEditMode ? 'Guardar cambios' : 'Completar registro')
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompletarDatos;
